/**
 * Comparison Stats Utility
 *
 * Provides detailed comparison statistics for candidate and cashflow baseline testing.
 * Handles matched, mismatched, new, and missing items.
 */

import type { CandidateSummary } from '@shared/models';
import type { CashFlow } from '~/types/candidate';
import { compareValues } from './DiffCell';

/**
 * Result of comparing a single candidate against its baseline
 */
export type CandidateComparisonStatus = 'matched' | 'mismatched' | 'new';

/**
 * Detailed comparison result for a single candidate
 */
export interface CandidateComparison {
  name: string;
  status: CandidateComparisonStatus;
  actual: CandidateSummary;
  expected: CandidateSummary | null;
  /** Which fields mismatched (only populated for 'mismatched' status) */
  mismatchedFields: string[];
}

/**
 * Missing candidate (in baseline but not in actual)
 */
export interface MissingCandidate {
  name: string;
  expected: CandidateSummary;
}

/**
 * Comprehensive comparison statistics
 */
export interface ComparisonStats {
  /** Candidates present in both actual and baseline with matching values */
  matched: CandidateComparison[];
  /** Candidates present in both but with different values */
  mismatched: CandidateComparison[];
  /** Candidates in actual but not in baseline (new candidates) */
  newCandidates: CandidateComparison[];
  /** Candidates in baseline but not in actual (removed/missing) */
  missing: MissingCandidate[];
  /** Summary counts */
  counts: {
    matched: number;
    mismatched: number;
    new: number;
    missing: number;
    total: number;
  };
  /** Overall status considering all factors */
  overallStatus: 'pass' | 'fail' | 'partial' | 'no-baseline';
}

/**
 * Fields to compare for determining if a candidate matches baseline
 */
const COMPARISON_FIELDS = [
  'viable',
  'totalPrincipal',
  'totalInterest',
  'totalRevenue',
  'revenueUtilization',
  'efficiency',
] as const;

/**
 * Compare a single candidate against its expected baseline
 */
function compareSingleCandidate(
  actual: CandidateSummary,
  expected: CandidateSummary | null
): CandidateComparison {
  if (!expected) {
    return {
      name: actual.name,
      status: 'new',
      actual,
      expected: null,
      mismatchedFields: [],
    };
  }

  const mismatchedFields: string[] = [];

  for (const field of COMPARISON_FIELDS) {
    const actualValue = actual[field];
    const expectedValue = expected[field];
    if (compareValues(actualValue, expectedValue) === 'mismatch') {
      mismatchedFields.push(field);
    }
  }

  return {
    name: actual.name,
    status: mismatchedFields.length > 0 ? 'mismatched' : 'matched',
    actual,
    expected,
    mismatchedFields,
  };
}

/**
 * Calculate comprehensive comparison statistics between actual candidates and baseline
 */
export function calculateComparisonStats(
  actualCandidates: CandidateSummary[],
  expectedCandidates: CandidateSummary[] | null
): ComparisonStats {
  // Handle no baseline case
  if (!expectedCandidates || expectedCandidates.length === 0) {
    const newComparisons = actualCandidates.map((actual) => ({
      name: actual.name,
      status: 'new' as const,
      actual,
      expected: null,
      mismatchedFields: [],
    }));

    return {
      matched: [],
      mismatched: [],
      newCandidates: newComparisons,
      missing: [],
      counts: {
        matched: 0,
        mismatched: 0,
        new: actualCandidates.length,
        missing: 0,
        total: actualCandidates.length,
      },
      overallStatus: 'no-baseline',
    };
  }

  // Create lookup map for expected candidates
  const expectedMap = new Map<string, CandidateSummary>();
  for (const exp of expectedCandidates) {
    expectedMap.set(exp.name, exp);
  }

  // Compare each actual candidate
  const matched: CandidateComparison[] = [];
  const mismatched: CandidateComparison[] = [];
  const newCandidates: CandidateComparison[] = [];
  const seenExpectedNames = new Set<string>();

  for (const actual of actualCandidates) {
    const expected = expectedMap.get(actual.name) ?? null;
    if (expected) {
      seenExpectedNames.add(actual.name);
    }

    const comparison = compareSingleCandidate(actual, expected);

    switch (comparison.status) {
      case 'matched':
        matched.push(comparison);
        break;
      case 'mismatched':
        mismatched.push(comparison);
        break;
      case 'new':
        newCandidates.push(comparison);
        break;
    }
  }

  // Find missing candidates (in baseline but not in actual)
  const missing: MissingCandidate[] = [];
  for (const exp of expectedCandidates) {
    if (!seenExpectedNames.has(exp.name)) {
      missing.push({ name: exp.name, expected: exp });
    }
  }

  // Calculate overall status
  let overallStatus: ComparisonStats['overallStatus'];
  if (mismatched.length > 0 || missing.length > 0) {
    // Any mismatch or missing = fail
    overallStatus = 'fail';
  } else if (newCandidates.length > 0 && matched.length > 0) {
    // Has new candidates but existing ones match = partial
    overallStatus = 'partial';
  } else if (newCandidates.length > 0 && matched.length === 0) {
    // Only new candidates, no baseline matches = effectively no baseline
    overallStatus = 'no-baseline';
  } else {
    // Everything matches
    overallStatus = 'pass';
  }

  return {
    matched,
    mismatched,
    newCandidates,
    missing,
    counts: {
      matched: matched.length,
      mismatched: mismatched.length,
      new: newCandidates.length,
      missing: missing.length,
      total: actualCandidates.length,
    },
    overallStatus,
  };
}

/**
 * Get a human-readable summary of comparison stats
 */
export function getComparisonSummary(stats: ComparisonStats): string {
  const parts: string[] = [];

  if (stats.counts.matched > 0) {
    parts.push(`${stats.counts.matched} matched`);
  }
  if (stats.counts.mismatched > 0) {
    parts.push(`${stats.counts.mismatched} mismatched`);
  }
  if (stats.counts.new > 0) {
    parts.push(`${stats.counts.new} new`);
  }
  if (stats.counts.missing > 0) {
    parts.push(`${stats.counts.missing} missing`);
  }

  if (parts.length === 0) {
    return 'No candidates';
  }

  return parts.join(', ');
}

// ============================================================================
// CASHFLOW COMPARISON STATS
// ============================================================================

/**
 * Result of comparing a single cashflow row against its baseline
 */
export type CashflowComparisonStatus = 'matched' | 'mismatched' | 'new';

/**
 * Detailed comparison result for a single cashflow row
 */
export interface CashflowComparison {
  paymentDate: string;
  status: CashflowComparisonStatus;
  actual: CashFlow;
  expected: CashFlow | null;
  /** Which fields mismatched (only populated for 'mismatched' status) */
  mismatchedFields: string[];
}

/**
 * Missing cashflow row (in baseline but not in actual)
 */
export interface MissingCashflow {
  paymentDate: string;
  expected: CashFlow;
}

/**
 * Comprehensive cashflow comparison statistics
 */
export interface CashflowComparisonStats {
  /** Cashflow rows present in both actual and baseline with matching values */
  matched: CashflowComparison[];
  /** Cashflow rows present in both but with different values */
  mismatched: CashflowComparison[];
  /** Cashflow rows in actual but not in baseline (new rows) */
  newRows: CashflowComparison[];
  /** Cashflow rows in baseline but not in actual (removed/missing) */
  missing: MissingCashflow[];
  /** Summary counts */
  counts: {
    matched: number;
    mismatched: number;
    new: number;
    missing: number;
    total: number;
  };
  /** Overall status considering all factors */
  overallStatus: 'pass' | 'fail' | 'partial' | 'no-baseline';
}

/**
 * Key numeric fields to compare for cashflow matching
 * (excludes date strings and complex nested objects)
 */
const CASHFLOW_COMPARISON_FIELDS = [
  'relatedTaxBillYear',
  'isFinalFiscalYearPayment',
  'isActive',
  'millageRate',
  'totalRevenueForTaxYear',
  'paymentRevenuePercentage',
  'paymentRevenuePortion',
  'drfStartingBalance',
  'debtPaymentAmount',
  'existingDebtPrincipal',
  'existingDebtInterest',
  'revenueMinusExistingDebt',
  'isInterestOnly',
  'interestObligation',
  'availableForBonds',
  'totalBondPayment',
  'drfAdjustment',
  'drfEndingBalance',
  'drfNetChange',
  'drfNetRevenue',
] as const;

/**
 * Compare a single cashflow row against its expected baseline
 */
function compareSingleCashflow(actual: CashFlow, expected: CashFlow | null): CashflowComparison {
  if (!expected) {
    return {
      paymentDate: actual.paymentDate,
      status: 'new',
      actual,
      expected: null,
      mismatchedFields: [],
    };
  }

  const mismatchedFields: string[] = [];

  for (const field of CASHFLOW_COMPARISON_FIELDS) {
    const actualValue = actual[field as keyof CashFlow];
    const expectedValue = expected[field as keyof CashFlow];
    if (
      typeof actualValue !== 'object' &&
      typeof expectedValue !== 'object' &&
      compareValues(
        actualValue as number | boolean | string,
        expectedValue as number | boolean | string
      ) === 'mismatch'
    ) {
      mismatchedFields.push(field);
    }
  }

  // Also compare bond payments if present
  if (actual.bondPayments && expected.bondPayments) {
    for (let i = 0; i < actual.bondPayments.length; i++) {
      const actualBp = actual.bondPayments[i];
      const expectedBp = expected.bondPayments[i];
      if (expectedBp) {
        if (compareValues(actualBp.principal, expectedBp.principal) === 'mismatch') {
          mismatchedFields.push(`bondPayments[${i}].principal`);
        }
        if (compareValues(actualBp.totalInterest, expectedBp.totalInterest) === 'mismatch') {
          mismatchedFields.push(`bondPayments[${i}].totalInterest`);
        }
        if (compareValues(actualBp.totalPayment, expectedBp.totalPayment) === 'mismatch') {
          mismatchedFields.push(`bondPayments[${i}].totalPayment`);
        }
      }
    }
  }

  return {
    paymentDate: actual.paymentDate,
    status: mismatchedFields.length > 0 ? 'mismatched' : 'matched',
    actual,
    expected,
    mismatchedFields,
  };
}

/**
 * Calculate comprehensive comparison statistics between actual cashflows and baseline
 */
export function calculateCashflowComparisonStats(
  actualCashflows: CashFlow[],
  expectedCashflows: CashFlow[] | null
): CashflowComparisonStats {
  // Handle no baseline case
  if (!expectedCashflows || expectedCashflows.length === 0) {
    const newComparisons = actualCashflows.map((actual) => ({
      paymentDate: actual.paymentDate,
      status: 'new' as const,
      actual,
      expected: null,
      mismatchedFields: [],
    }));

    return {
      matched: [],
      mismatched: [],
      newRows: newComparisons,
      missing: [],
      counts: {
        matched: 0,
        mismatched: 0,
        new: actualCashflows.length,
        missing: 0,
        total: actualCashflows.length,
      },
      overallStatus: 'no-baseline',
    };
  }

  // Create lookup map for expected cashflows by payment date
  const expectedMap = new Map<string, CashFlow>();
  for (const exp of expectedCashflows) {
    expectedMap.set(exp.paymentDate, exp);
  }

  // Compare each actual cashflow
  const matched: CashflowComparison[] = [];
  const mismatched: CashflowComparison[] = [];
  const newRows: CashflowComparison[] = [];
  const seenExpectedDates = new Set<string>();

  for (const actual of actualCashflows) {
    const expected = expectedMap.get(actual.paymentDate) ?? null;
    if (expected) {
      seenExpectedDates.add(actual.paymentDate);
    }

    const comparison = compareSingleCashflow(actual, expected);

    switch (comparison.status) {
      case 'matched':
        matched.push(comparison);
        break;
      case 'mismatched':
        mismatched.push(comparison);
        break;
      case 'new':
        newRows.push(comparison);
        break;
    }
  }

  // Find missing cashflows (in baseline but not in actual)
  const missing: MissingCashflow[] = [];
  for (const exp of expectedCashflows) {
    if (!seenExpectedDates.has(exp.paymentDate)) {
      missing.push({ paymentDate: exp.paymentDate, expected: exp });
    }
  }

  // Calculate overall status
  let overallStatus: CashflowComparisonStats['overallStatus'];
  if (mismatched.length > 0 || missing.length > 0) {
    overallStatus = 'fail';
  } else if (newRows.length > 0 && matched.length > 0) {
    overallStatus = 'partial';
  } else if (newRows.length > 0 && matched.length === 0) {
    overallStatus = 'no-baseline';
  } else {
    overallStatus = 'pass';
  }

  return {
    matched,
    mismatched,
    newRows,
    missing,
    counts: {
      matched: matched.length,
      mismatched: mismatched.length,
      new: newRows.length,
      missing: missing.length,
      total: actualCashflows.length,
    },
    overallStatus,
  };
}

/**
 * Get a human-readable summary of cashflow comparison stats
 */
export function getCashflowComparisonSummary(stats: CashflowComparisonStats): string {
  const parts: string[] = [];

  if (stats.counts.matched > 0) {
    parts.push(`${stats.counts.matched} matched`);
  }
  if (stats.counts.mismatched > 0) {
    parts.push(`${stats.counts.mismatched} mismatched`);
  }
  if (stats.counts.new > 0) {
    parts.push(`${stats.counts.new} new`);
  }
  if (stats.counts.missing > 0) {
    parts.push(`${stats.counts.missing} missing`);
  }

  if (parts.length === 0) {
    return 'No cashflows';
  }

  return parts.join(', ');
}
